import React from "react"
import { motion } from "framer-motion"
import {X, Printer} from 'lucide-react'
import { Aforamento } from "../context/GuiasContext"
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"

interface VisualizarAforamentoProps {
  aforamento: Aforamento
  onClose: () => void
}

export default function VisualizarAforamento({ aforamento, onClose }: VisualizarAforamentoProps) {
  const { logoUrl, dadosPrefeitura } = useGuias()

  const handlePrint = () => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      toast.error("Não foi possível abrir a janela de impressão")
      return
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Documento de Aforamento - ${aforamento.numero}</title>
          <style>
            body { 
              margin: 0; 
              padding: 40px; 
              font-family: Arial, sans-serif;
            }
            @media print {
              body { margin: 0; padding: 15mm; }
              @page { size: A4; margin: 10mm; }
            }
            .header {
              display: flex;
              gap: 24px;
              margin-bottom: 24px;
              padding-bottom: 24px;
              border-bottom: 2px solid #d1d5db;
            }
            .header img {
              height: 80px;
              width: auto;
              object-fit: contain;
            }
            .header-info {
              flex: 1;
            }
            .header-info h2 {
              font-size: 20px;
              font-weight: bold;
              color: #1f2937;
              margin: 0 0 8px 0;
            }
            .header-info p {
              font-size: 12px;
              color: #4b5563;
              margin: 2px 0;
            }
            .title {
              font-size: 18px;
              font-weight: bold;
              text-align: center;
              color: #1f2937;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            .section {
              margin-bottom: 16px;
            }
            .section h4 {
              font-size: 14px;
              font-weight: 600;
              color: #374151;
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 1px solid #d1d5db;
            }
            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
            }
            .field {
              margin-bottom: 8px;
            }
            .field-label {
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 2px;
            }
            .field-value {
              font-size: 12px;
              font-weight: 500;
              color: #1f2937;
            }
            .full-width {
              grid-column: 1 / -1;
            }
            .signatures {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 40px;
            }
            .signature-box {
              text-align: center;
              border-top: 2px solid #9ca3af;
              padding-top: 8px;
            }
            .signature-box p {
              font-size: 11px;
              margin: 4px 0;
            }
            .signature-title {
              font-weight: 500;
              color: #374151;
            }
            .signature-subtitle {
              color: #6b7280;
            }
            .footer {
              margin-top: 32px;
              text-align: center;
              font-size: 10px;
              color: #6b7280;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logoUrl}" alt="Logo do Município" />
            <div class="header-info">
              <h2>${dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}</h2>
              <p>${dadosPrefeitura?.secretaria || ""}</p>
              <p>${dadosPrefeitura?.departamento || ""}</p>
              <p>${dadosPrefeitura?.endereco || ""} | CEP: ${dadosPrefeitura?.cep || ""}</p>
              <p>CNPJ: ${dadosPrefeitura?.cnpj || ""} | Tel: ${dadosPrefeitura?.telefone || ""}</p>
            </div>
          </div>
          
          <h3 class="title">DOCUMENTO DE AFORAMENTO Nº ${aforamento.numero}</h3>
          
          <div class="section">
            <h4>Informações do Proprietário</h4>
            <div class="grid">
              <div class="field">
                <p class="field-label">Nome do Proprietário:</p>
                <p class="field-value">${aforamento.proprietario}</p>
              </div>
              <div class="field">
                <p class="field-label">CPF:</p>
                <p class="field-value">${aforamento.cpfProprietario}</p>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h4>Informações da Propriedade</h4>
            <div class="grid">
              <div class="field full-width">
                <p class="field-label">Denominação da Propriedade:</p>
                <p class="field-value">${aforamento.denominacao}</p>
              </div>
              <div class="field">
                <p class="field-label">Região:</p>
                <p class="field-value">${aforamento.regiao}</p>
              </div>
              <div class="field">
                <p class="field-label">Valor Venal:</p>
                <p class="field-value">R$ ${aforamento.valorVenal}</p>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h4>Informações do Pagamento</h4>
            <div class="grid">
              <div class="field">
                <p class="field-label">Ano:</p>
                <p class="field-value">${aforamento.ano}</p>
              </div>
              <div class="field">
                <p class="field-label">Imposto a Pagar:</p>
                <p class="field-value">R$ ${aforamento.impostoAPagar}</p>
              </div>
              <div class="field">
                <p class="field-label">Nº do Talão:</p>
                <p class="field-value">${aforamento.numeroTalao}</p>
              </div>
              <div class="field">
                <p class="field-label">Data do Pagamento:</p>
                <p class="field-value">${aforamento.dataPagamento ? new Date(aforamento.dataPagamento).toLocaleDateString("pt-BR") : ""}</p>
              </div>
              ${aforamento.observacao ? `
              <div class="field full-width">
                <p class="field-label">Observação:</p>
                <p class="field-value">${aforamento.observacao}</p>
              </div>
              ` : ""}
            </div>
          </div>
          
          <div class="signatures">
            <div class="signature-box">
              <p class="signature-title">Assinatura do Funcionário</p>
              <p class="signature-subtitle">${dadosPrefeitura?.secretaria || "Secretaria Municipal"}</p>
            </div>
            <div class="signature-box">
              <p class="signature-title">Assinatura do Requerente</p>
              <p class="signature-subtitle">${aforamento.proprietario}</p>
            </div>
          </div>
          
          <div class="footer">
            <p>Documento emitido em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print()
              }, 500)
            }
          </script>
        </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
    toast.success("Documento enviado para impressão")
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-gray-800">Visualizar Aforamento</h3>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrint}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              title="Imprimir Aforamento"
            >
              <Printer className="w-5 h-5" />
              <span className="text-sm font-medium">Imprimir</span>
            </motion.button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* Cabeçalho com Logo */}
          <div className="flex items-start gap-6 mb-6 pb-6 border-b-2 border-gray-300">
            <img
              src={logoUrl}
              alt="Logo do Município"
              className="h-24 w-auto object-contain"
            />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}
              </h2>
              <p className="text-sm text-gray-600">{dadosPrefeitura?.secretaria}</p>
              <p className="text-sm text-gray-600">{dadosPrefeitura?.departamento}</p>
              <p className="text-sm text-gray-600 mt-2">
                {dadosPrefeitura?.endereco} | CEP: {dadosPrefeitura?.cep}
              </p>
              <p className="text-sm text-gray-600">
                CNPJ: {dadosPrefeitura?.cnpj} | Tel: {dadosPrefeitura?.telefone}
              </p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-center text-gray-800 mb-6 uppercase">
            DOCUMENTO DE AFORAMENTO Nº {aforamento.numero}
          </h3>

          {/* Informações do Proprietário */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-300">
              Informações do Proprietário
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-sm text-gray-600">Nome do Proprietário:</p>
                <p className="font-medium text-gray-800">{aforamento.proprietario}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CPF:</p>
                <p className="font-medium text-gray-800">{aforamento.cpfProprietario}</p>
              </div>
            </div>
          </div>

          {/* Informações da Propriedade */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-300">
              Informações da Propriedade
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Denominação da Propriedade:</p>
                <p className="font-medium text-gray-800">{aforamento.denominacao}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Região:</p>
                <p className="font-medium text-gray-800">{aforamento.regiao}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valor Venal:</p>
                <p className="font-medium text-gray-800">R$ {aforamento.valorVenal}</p>
              </div>
            </div>
          </div>

          {/* Informações do Pagamento */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-300">
              Informações do Pagamento
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-sm text-gray-600">Ano:</p>
                <p className="font-medium text-gray-800">{aforamento.ano}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Imposto a Pagar:</p>
                <p className="font-medium text-gray-800">R$ {aforamento.impostoAPagar}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Nº do Talão:</p>
                <p className="font-medium text-gray-800">{aforamento.numeroTalao}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Data do Pagamento:</p>
                <p className="font-medium text-gray-800">
                  {aforamento.dataPagamento ? new Date(aforamento.dataPagamento).toLocaleDateString("pt-BR") : ""}
                </p>
              </div>
              {aforamento.observacao && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Observação:</p>
                  <p className="font-medium text-gray-800">{aforamento.observacao}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assinaturas */}
          <div className="grid grid-cols-2 gap-8 mt-12">
            <div className="text-center">
              <div className="border-t-2 border-gray-400 pt-2">
                <p className="text-sm font-medium text-gray-700">Assinatura do Funcionário</p>
                <p className="text-xs text-gray-500 mt-1">{dadosPrefeitura?.secretaria || "Secretaria Municipal"}</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t-2 border-gray-400 pt-2">
                <p className="text-sm font-medium text-gray-700">Assinatura do Requerente</p>
                <p className="text-xs text-gray-500 mt-1">{aforamento.proprietario}</p>
              </div>
            </div>
          </div>

          {/* Rodapé */}
          <div className="mt-8 text-center text-xs text-gray-500">
            <p>Documento emitido em {new Date().toLocaleDateString("pt-BR")} às {new Date().toLocaleTimeString("pt-BR")}</p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
